import React, { FunctionComponent, useEffect, useState } from 'react'
import { VerifyIdentity } from './VerifyIdentity'
import { FirstPurchase } from './FirstPurchase'
import { Step } from './Step'

import { isUnsuitabilityRequiredError, useNewUserEffects, useNewUserLocalization } from './NewUser.hooks'
import clsx from 'clsx'
import { Panel, Skeleton, useIsBreakpointLargeAndUp, useToast } from '@afterpaytouch/core'
import styles from './style.module.scss'
import { AddCardModal } from '../../AccountTabs/PaymentMethods/AddCardModal'
import { useRouter } from 'next/router'

import { useAmplitudeWithEnduringEventProperties } from '../../../integrations/amplitude'
import { TrackingEvent } from '../../../model/amplitude'
import { useStepOneTaskStatus } from './useStepOneTaskStatus'
import { Route } from '../../../config/router'
import { ExtendedPanel } from '../../Panel'
import { Unsuitability } from './Unsuitability/Unsuitability'

export const NewUser: FunctionComponent = () => {
  const [isAddPaymentModalOpen, setIsAddPaymentModalOpen] = useState(false)
  const isBpLargeUp = useIsBreakpointLargeAndUp()
  const classNames = clsx(isBpLargeUp === true && styles.bpLargeUp)
  const { t } = useNewUserLocalization()
  const { consumer, creditLimitErrorCode, isLoading, isFetching, isError, refetchNewUser, isTerminalError } = useNewUserEffects()
  const router = useRouter()
  const setToast = useToast()
  const isSkeletonShown = isLoading || isFetching

  const { logEvent } = useAmplitudeWithEnduringEventProperties()

  const openAddPaymentModal = (): void => setIsAddPaymentModalOpen(true)
  const closeAddPaymentModal = (): void => setIsAddPaymentModalOpen(false)
  const [showErrorPanel, setShowErrorPanel] = useState(false)
  const identityCheckStatus = consumer.identityCheckStatus
  const { stepOneStatus, ctaLoading } = useStepOneTaskStatus({
    creditLimitErrorCode,
    identityCheckStatus,
  })
  const isStepOneComplete = stepOneStatus?.isCompleted
  const isStepTwoComplete = isStepOneComplete && consumer.hasPreferredPaymentMethod
  const isStepThreeComplete = isStepTwoComplete && consumer.hasTransactions

  useEffect(() => {
    !isStepTwoComplete && logEvent(TrackingEvent.VIEWED_ADD_PAYMENT_METHOD_BUTTON_ON_HOME_PAGE)
  }, [isStepTwoComplete])

  useEffect(() => {
    if (isTerminalError) {
      creditLimitErrorCode && router.push(`${Route.TERMINAL_ERROR}/${creditLimitErrorCode}`)
    }
  }, [creditLimitErrorCode, isTerminalError])

  useEffect(() => {
    // Do not show error toast if skeleton is shown, if it's a terminal error, or if it's an unsuitability required error
    if (isError && !isSkeletonShown && !isTerminalError && !isUnsuitabilityRequiredError(creditLimitErrorCode)) {
      setShowErrorPanel(true)
      setToast({
        message: t('common:error.defaultErrorMsg'),
        kind: 'Failure',
      })
      logEvent(TrackingEvent.VIEWED_ERROR_TOAST_ON_HOME_PAGE, { errorCode: creditLimitErrorCode })
    } else {
      setShowErrorPanel(false)
    }
  }, [isError, isSkeletonShown, isTerminalError])

  const stepsClassName = clsx(styles.steps, isSkeletonShown ? styles.invisible : styles.visible)

  const openShopPage = (): void => {
    const url = t('common:urls:afterpaySite')
    logEvent(TrackingEvent.CLICKED_SHOP_NOW, { outboundLink: url })
    window.open(url, '_blank')
  }

  if (showErrorPanel) {
    return <ExtendedPanel.Error onClick={refetchNewUser} />
  }
  return (
    <>
      <div className={classNames} data-testid='new-cx-welcome-panel'>
        <ExtendedPanel.HeadWithCrumb>
          <div className={styles.headContainer}>
            {isUnsuitabilityRequiredError(creditLimitErrorCode) ? (
              <Unsuitability consumer={consumer} isSkeletonShown={isSkeletonShown} />
            ) : consumer.isVerified ? (
              <FirstPurchase consumer={consumer} isSkeletonShown={isSkeletonShown} isParentLoading={isLoading || isFetching} />
            ) : (
              <VerifyIdentity consumer={consumer} isSkeletonShown={isSkeletonShown} />
            )}
          </div>
        </ExtendedPanel.HeadWithCrumb>
        {/* @ts-ignore: OPERATION BLEED STOPPER */}
        <Panel.Content>
          <div className={styles.contentSkeletonContainer}>
            {isSkeletonShown && <Skeleton kind='Default' />}
            <div className={stepsClassName}>
              <Step
                badge={t('common:numbers:one')}
                task={stepOneStatus.task}
                subtask={stepOneStatus.subtask}
                completed={isStepOneComplete}
                testNameSpace='verify-identity-step'
                cta={stepOneStatus.cta}
                ctaLoading={ctaLoading}
              />
              <div className={styles.divider} />
              <Step
                badge={t('common:numbers:two')}
                task={isStepTwoComplete ? t('index:welcome:steps:payment:task-completed') : t('index:welcome:steps:payment:task')}
                subtask={isStepTwoComplete ? t('index:welcome:steps:payment:subtask-completed') : t('index:welcome:steps:payment:subtask')}
                completed={isStepTwoComplete}
                lastStepCompleted={isStepOneComplete}
                testNameSpace='new-cx-add-payment-method'
                cta={{
                  label: t('index:welcome:steps:payment:cta:label'),
                  onClick: () => {
                    logEvent(TrackingEvent.PRESSED_ADD_PAYMENT_METHOD_ON_HOME_PAGE)
                    openAddPaymentModal()
                  },
                }}
              />
              <div className={styles.divider} />
              <Step
                badge={t('common:numbers:three')}
                task={t('index:welcome:steps:purchase:task')}
                subtask={t('index:welcome:steps:purchase:subtask')}
                completed={isStepThreeComplete}
                lastStepCompleted={isStepOneComplete}
                testNameSpace='new-cx-shop'
                cta={{
                  label: t('index:welcome:steps:purchase:cta:label'),
                  onClick: openShopPage,
                }}
              />
            </div>
          </div>
        </Panel.Content>
      </div>
      <AddCardModal show={isAddPaymentModalOpen} onClose={closeAddPaymentModal} />
    </>
  )
}
